/**
 * MultiSelectMenu — a trigger button with a popover checklist (FRIDAY-105).
 *
 * The design-system multi-select. Used for a task's labels and for the boards
 * a label is exposed on. Options can carry a colour dot; an optional search
 * box filters them and an optional "Create" row adds a new option from the
 * search text.
 *
 * Keyboard: the popover opens on the search box (or the first option),
 * ArrowUp / ArrowDown move between options, Space or Enter toggles, Escape
 * closes and returns focus to the trigger. A click outside closes it.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface MultiSelectOption {
  id: string
  label: string
  /** Optional colour dot before the label. */
  color?: string
  /** Optional muted text after the label. */
  hint?: string
}

export interface MultiSelectMenuProps {
  options: readonly MultiSelectOption[]
  /** Selected option ids. */
  value: readonly string[]
  onChange: (next: string[]) => void
  /** Trigger content. */
  trigger: ReactNode
  /** Accessible name of the trigger and the list. */
  label: string
  disabled?: boolean
  /** Show a search box above the options. */
  searchable?: boolean
  /** Show "Create “text”" when the search text matches no option. */
  onCreate?: (name: string) => void
  /** Text shown when there are no options at all. */
  emptyText?: string
  /** A row above the options, e.g. "All boards". Toggling it calls onToggleAll. */
  allLabel?: string
  allSelected?: boolean
  onToggleAll?: () => void
  className?: string
  triggerClassName?: string
}

export function MultiSelectMenu({
  options,
  value,
  onChange,
  trigger,
  label,
  disabled,
  searchable,
  onCreate,
  emptyText = 'Nothing to pick.',
  allLabel,
  allSelected,
  onToggleAll,
  className,
  triggerClassName,
}: MultiSelectMenuProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const menuId = useId()

  const close = (restoreFocus = false) => {
    setOpen(false)
    setQuery('')
    if (restoreFocus) requestAnimationFrame(() => triggerRef.current?.focus())
  }

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        close(true)
      }
    }
    if (searchable) searchRef.current?.focus()
    else listRef.current?.querySelector<HTMLElement>('[role="menuitemcheckbox"]')?.focus()
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, searchable])

  const term = query.trim()
  const visible = term
    ? options.filter((o) => o.label.toLowerCase().includes(term.toLowerCase()))
    : options
  const canCreate =
    !!onCreate && term.length > 0 && !options.some((o) => o.label.toLowerCase() === term.toLowerCase())

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])
  }

  /** Arrow keys move between the rows of the list. */
  const onListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    const rows = [...(listRef.current?.querySelectorAll<HTMLElement>('[data-menu-row]') ?? [])]
    if (!rows.length) return
    const at = rows.indexOf(document.activeElement as HTMLElement)
    const next = event.key === 'ArrowDown' ? Math.min(rows.length - 1, at + 1) : Math.max(0, at - 1)
    rows[at === -1 ? 0 : next]?.focus()
  }

  return (
    <div ref={rootRef} className={cn('relative inline-block', className)}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        className={cn(
          'flex min-h-8 items-center gap-1.5 rounded-md border border-surface-300 bg-white px-2.5 py-1 text-xs font-medium hover:border-brand-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-surface-700 dark:bg-surface-800',
          triggerClassName,
        )}
      >
        {trigger}
        <span className="text-[10px] text-surface-500" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div
          id={menuId}
          className="absolute z-20 mt-1 w-64 rounded-md border border-surface-200 bg-white py-1 shadow-lg dark:border-surface-700 dark:bg-surface-800"
          onKeyDown={onListKeyDown}
        >
          {searchable && (
            <div className="px-2 pb-1 pt-1">
              <input
                ref={searchRef}
                data-menu-row
                type="search"
                className="input h-8 py-0 text-xs"
                placeholder={onCreate ? 'Search or create…' : 'Search…'}
                aria-label={`Search ${label}`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canCreate) {
                    e.preventDefault()
                    onCreate!(term)
                    setQuery('')
                  }
                }}
              />
            </div>
          )}
          <div ref={listRef} role="menu" aria-label={label} className="max-h-64 overflow-y-auto">
            {allLabel && onToggleAll && !term && (
              <>
                <Row label={allLabel} checked={!!allSelected} onToggle={onToggleAll} />
                <div className="my-1 border-t border-surface-200 dark:border-surface-700" />
              </>
            )}
            {visible.map((o) => (
              <Row
                key={o.id}
                label={o.label}
                color={o.color}
                hint={o.hint}
                checked={value.includes(o.id)}
                onToggle={() => toggle(o.id)}
              />
            ))}
            {canCreate && (
              <button
                type="button"
                role="menuitem"
                data-menu-row
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-brand-700 hover:bg-surface-100 focus:bg-surface-100 focus:outline-none dark:text-brand-200 dark:hover:bg-surface-700 dark:focus:bg-surface-700"
                onClick={() => {
                  onCreate!(term)
                  setQuery('')
                }}
              >
                + Create “{term}”
              </button>
            )}
            {!visible.length && !canCreate && (
              <div className="px-3 py-2 text-xs text-surface-500">{term ? 'No match.' : emptyText}</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function Row({
  label,
  color,
  hint,
  checked,
  onToggle,
}: {
  label: string
  color?: string
  hint?: string
  checked: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      data-menu-row
      onClick={onToggle}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-surface-100 focus:bg-surface-100 focus:outline-none dark:hover:bg-surface-700 dark:focus:bg-surface-700',
        checked && 'text-brand-700 dark:text-brand-200',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border text-[9px]',
          checked ? 'border-brand-500 bg-brand-500 text-white' : 'border-surface-300 dark:border-surface-600',
        )}
      >
        {checked ? '✓' : ''}
      </span>
      {color && <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {hint && <span className="shrink-0 text-[10px] text-surface-500">{hint}</span>}
    </button>
  )
}
